import React, { useEffect, useState } from "react";
import { NodeViewWrapper } from "@tiptap/react";
import { FileOutlined, UserOutlined, RocketOutlined, CalendarOutlined, LinkOutlined, DeleteOutlined, EnvironmentOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { message } from "antd";
import { getPresignedViewUrl } from "../../../api/attachmentApi";
import "./LinkedContentNodeView.css";

/**
 * LinkedContentNodeView组件
 *
 * 此组件负责在TipTap编辑器中渲染链接内容节点
 * 支持多种内容类型：航班、附件、联系人、事件和通用链接
 */

// 解析内容数据的工具函数
const parseContentData = (jsonStr) => {
    try {
        return typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
    } catch (error) {
        console.error("解析内容数据失败:", error);
        return { type: "unknown", data: {} };
    }
};

// 内容类型图标映射
const CONTENT_TYPE_ICONS = {
    flight: <RocketOutlined />,
    attachment: <FileOutlined />,
    contact: <UserOutlined />,
    event: <CalendarOutlined />,
    link: <LinkOutlined />,
};

// 获取解析后的显示参数
const getDisplayParams = (jsonStr) => {
    try {
        return typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr || {};
    } catch (error) {
        console.error("解析显示参数失败:", error);
        return { visible: true, showDetails: false, compact: false, highlight: false };
    }
};

/**
 * 航班信息表格组件 - 支持显示多条航班记录
 */
const FlightInfoTable = ({ flight, displayParams }) => {
    // 尝试获取多条航班数据
    const flights = displayParams?.flights || [];
    // 如果有传入单独的航班数据且没有flights数组，则将其作为唯一的航班显示
    const flightsToDisplay = flights.length > 0 ? flights : flight ? [flight] : [];

    // 检查数据结构并打印
    console.log("航班表格数据:", { displayParams, flights, flightsToDisplay });

    if (flightsToDisplay.length === 0)
        return (
            <div className="empty-data">
                <div>暂无航班数据</div>
            </div>
        );

    return (
        <div className="flight-info-table-container">
            <div className="flight-info-header">
                <RocketOutlined className="header-icon" />
                <span className="header-title">航班信息 ({flightsToDisplay.length}条)</span>
            </div>

            <table className="flight-info-table">
                <thead>
                    <tr>
                        <th>航班号</th>
                        <th>航线</th>
                        <th>起飞</th>
                        <th>到达</th>
                        <th>状态</th>
                    </tr>
                </thead>
                <tbody>
                    {flightsToDisplay.map((flight, index) => {
                        console.log(`航班行 ${index}:`, flight);
                        return (
                            <tr key={flight.id || index} className={index % 2 === 0 ? "even-row" : "odd-row"}>
                                <td className="flight-cell">{flight.flightNumber || "未知"}</td>
                                <td className="flight-cell">{`${flight.departureCode || "?"} → ${flight.arrivalCode || "?"}`}</td>
                                <td className="flight-cell">{flight.departureTime || "未知"}</td>
                                <td className="flight-cell">{flight.arrivalTime || "未知"}</td>
                                <td className="flight-cell">{flight.status || "-"}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

/**
 * 附件详情组件
 */
const AttachmentDetails = ({ attachment, onView }) => {
    if (!attachment) return null;

    const handleView = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            if (onView) {
                await onView(attachment);
                return;
            }

            // 回退逻辑：直接打开已有URL（不建议，但确保功能可用）
            const fallbackUrl = attachment.download_url || attachment.url || attachment.attachment_url;
            if (fallbackUrl) {
                window.open(fallbackUrl, "_blank", "noopener,noreferrer");
            } else {
                message.warning("该附件暂无可用链接");
            }
        } catch (err) {
            console.warn("打开附件失败:", err);
            const fallbackUrl = attachment.download_url || attachment.url || attachment.attachment_url;
            if (fallbackUrl) {
                window.open(fallbackUrl, "_blank", "noopener,noreferrer");
            } else {
                message.error("无法打开附件链接");
            }
        }
    };

    return (
        <div className="attachment-details">
            <div className="attachment-name">{attachment.fileName || "未知文件"}</div>
            <div className="attachment-meta">
                {attachment.fileSize && <span className="attachment-size">{attachment.fileSize}</span>}
                {attachment.fileType && <span className="attachment-type">{attachment.fileType}</span>}
            </div>
            {(attachment.download_url || attachment.url || attachment.attachment_url || attachment.object_key) && (
                <div className="attachment-url">
                    <a href="#" onClick={handleView} role="button">
                        查看文件
                    </a>
                </div>
            )}
        </div>
    );
};

/**
 * 联系人详情组件
 */
const ContactDetails = ({ contact }) => {
    if (!contact) return null;

    return (
        <div className="contact-details">
            <div className="contact-name">{contact.name || "未知联系人"}</div>
            {contact.title && <div className="contact-title">{contact.title}</div>}
            <div className="contact-info">
                {contact.email && (
                    <div className="contact-email">
                        <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    </div>
                )}
                {contact.phone && <div className="contact-phone">{contact.phone}</div>}
            </div>
        </div>
    );
};

/**
 * 事件详情组件
 */
const EventDetails = ({ event }) => {
    if (!event) return null;

    return (
        <div className="event-details">
            <div className="event-title">{event.title || "未知事件"}</div>
            <div className="event-info">
                {(event.date || event.time) && (
                    <div className="event-time">
                        <ClockCircleOutlined style={{ marginRight: "4px" }} />
                        {event.date || ""} {event.time || ""}
                    </div>
                )}
                {event.location && (
                    <div className="event-location">
                        <EnvironmentOutlined style={{ marginRight: "4px" }} />
                        {event.location}
                    </div>
                )}
            </div>
        </div>
    );
};

export default function LinkedContentNodeView({ node, deleteNode }) {
    const [contentData, setContentData] = useState(null);
    const [displayParams, setDisplayParams] = useState({ visible: true, showDetails: false });

    useEffect(() => {
        if (node.attrs.data) {
            setContentData(parseContentData(node.attrs.data));
        }

        if (node.attrs.displayParams) {
            setDisplayParams(getDisplayParams(node.attrs.displayParams));
        }
    }, [node.attrs.data, node.attrs.displayParams]);

    if (!contentData) {
        return null;
    }

    const { type = "link", data = {} } = contentData;
    const { showDetails = false } = displayParams;

    // 根据内容类型生成标题
    const generateTitle = () => {
        switch (type) {
            case "flight":
                return `${data.flightNumber || "未知航班"} (${data.departureCode || "?"} → ${data.arrivalCode || "?"})`;
            case "attachment":
                return data.fileName || "附件";
            case "contact":
                return data.name || "联系人";
            case "event":
                return data.title || "日程事件";
            default:
                return data.title || "链接内容";
        }
    };

    // 根据内容类型生成描述
    const generateDescription = () => {
        switch (type) {
            case "flight":
                return `${data.departureTime || "未知时间"} - ${data.arrivalTime || "未知时间"}`;
            case "attachment":
                return `${data.fileSize || "未知大小"} | ${data.fileType || "未知类型"}`;
            case "contact":
                return data.title || data.email || "";
            case "event":
                return data.time || data.date || "";
            default:
                return data.description || "";
        }
    };

    // 获取URL或其他标识符
    const getUrlOrIdentifier = () => {
        switch (type) {
            case "flight":
                return `航班ID: ${data.id || "未知ID"}`;
            case "attachment":
                return data.url || "";
            case "contact":
                return data.email || data.phone || "";
            case "event":
                return data.location || "";
            default:
                return data.url || "";
        }
    };

    // 获取图标
    const getIcon = () => {
        return CONTENT_TYPE_ICONS[type] || <LinkOutlined />;
    };

    // 处理删除节点
    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (deleteNode) {
            deleteNode();
        }
    };

    // 安全打开附件（预签名查看链接）
    const openAttachment = async (attachment) => {
        if (!attachment) {
            message.warning("附件信息缺失");
            return;
        }

        try {
            // 优先使用对象键
            let objectKey = attachment.object_key;
            let rawUrl = attachment.download_url || attachment.url || attachment.attachment_url;

            // 如无对象键，尝试从URL解析
            if (!objectKey && rawUrl) {
                try {
                    const u = new URL(rawUrl);
                    objectKey = decodeURIComponent(u.pathname.replace(/^\//, ""));
                } catch (e) {
                    // 可能是相对路径或直接的对象键
                    objectKey = String(rawUrl).replace(/^\//, "");
                }
            }

            if (objectKey) {
                const data = await getPresignedViewUrl(objectKey);
                if (data?.signed_url) {
                    window.open(data.signed_url, "_blank", "noopener,noreferrer");
                    return;
                }
            }

            // 回退：直接打开原URL
            if (rawUrl) {
                window.open(rawUrl, "_blank", "noopener,noreferrer");
            } else {
                message.warning("该附件暂无可用链接");
            }
        } catch (error) {
            console.warn("获取附件预签名链接失败，回退到原链接:", error);
            const fallbackUrl = attachment.download_url || attachment.url || attachment.attachment_url;
            if (fallbackUrl) {
                window.open(fallbackUrl, "_blank", "noopener,noreferrer");
            } else {
                message.error("无法打开附件链接");
            }
        }
    };

    // 渲染详细视图
    const renderDetailedView = () => {
        // 特殊处理航班列表的情况
        if (node.attrs.uniqueId === "flightInfo-list") {
            return <FlightInfoTable flight={null} displayParams={displayParams} />;
        }

        switch (type) {
            case "flight":
                return <FlightInfoTable flight={data} displayParams={displayParams} />;
            case "attachment":
                return <AttachmentDetails attachment={data} onView={openAttachment} />;
            case "contact":
                return <ContactDetails contact={data} />;
            case "event":
                return <EventDetails event={data} />;
            default:
                return null;
        }
    };

    // 渲染简洁视图 (默认)
    const renderCompactView = () => {
        const url = getUrlOrIdentifier();
        const isLinkType = type === "link" && url;
        const isAttachmentType = type === "attachment";

        // 处理点击事件，仅对 link 类型生效
        const handleClick = (e) => {
            if (isLinkType) {
                e.stopPropagation(); // 阻止事件冒泡，防止触发编辑器的其他行为
                window.open(url, "_blank", "noopener,noreferrer");
            }
        };

        return (
            <div
                className={`linked-content-container ${type} ${isLinkType ? "clickable" : ""}`}
                onClick={handleClick} // 在容器上添加点击事件
                role={isLinkType ? "button" : undefined} // 为可点击元素添加角色
                tabIndex={isLinkType ? 0 : undefined} // 使可点击元素可通过键盘聚焦
                style={isLinkType ? { cursor: "pointer" } : {}} // 添加指针样式
            >
                <div className="linked-content-icon-wrapper">
                    <div className={`linked-content-icon ${type}`}>{getIcon()}</div>
                </div>
                <div className="linked-content-details">
                    <div className="linked-content-title">{generateTitle()}</div>
                    {generateDescription() && <div className="linked-content-description">{generateDescription()}</div>}
                    {url && (
                        <div className="linked-content-url">
                            {/* 对于 link 类型，显示为可点击的文本；其他类型仅显示文本 */}
                            {isLinkType ? <span className="link-text">{url}</span> : url}
                        </div>
                    )}
                    {isAttachmentType && (
                        <div className="linked-content-url">
                            <a
                                href="#"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openAttachment(data);
                                }}
                                role="button"
                            >
                                查看文件
                            </a>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // 使用 NodeViewWrapper 包裹整个组件的输出
    return (
        <NodeViewWrapper className={`linked-content-node-wrapper type-${type} ${showDetails ? "details-mode" : "compact-mode"}`}>
            {showDetails ? renderDetailedView() : renderCompactView()}
            {/* 删除按钮放在Wrapper内部，确保事件能被正确处理 */}
            <button
                className="delete-button"
                contentEditable="false" // 重要：防止按钮本身可编辑
                onClick={handleDelete}
                title="删除内容"
            >
                <DeleteOutlined />
            </button>
        </NodeViewWrapper>
    );
}
